// The readout clock (IMPLEMENTATION §0.2 R9 Gallery readout, R17 busy share): one-second buckets of
// `galleryReadout.bucketTicks`, `buckets` closed buckets plus one open slot per ring. During tick t
// (t ≥ 1) the open slot is floor((t−1)/B) mod (buckets+1); after a tick with t mod B = 0 the next slot opens
// and is zeroed. A query after tick T reads every slot except the open one of tick T+1: the last `buckets`
// whole seconds, never a partial one, so an exactly periodic supply reads exactly (e.g. 8 per 400 ticks).
import { floorDiv } from './core/num.ts';
import type { Int } from './core/types.ts';
import type { SimDefs } from './defs.ts';

export function ringSlots(d: SimDefs): Int {
  return d.economy.galleryReadout.buckets + 1;
}

/** A fresh ring (all zeros). */
export function zeroRing(d: SimDefs): Int[] {
  const a: Int[] = [];
  for (let i = 0; i < ringSlots(d); i++) a.push(0);
  return a;
}

/** The open slot while tick `t` (≥ 1) is processed. */
export function openSlot(d: SimDefs, t: Int): Int {
  return floorDiv(t - 1, d.economy.galleryReadout.bucketTicks) % ringSlots(d);
}

/** True when tick `t` closes a bucket (the next tick opens a new slot). */
export function closesBucket(d: SimDefs, t: Int): boolean {
  return t % d.economy.galleryReadout.bucketTicks === 0;
}

/** The slot tick t + 1 accumulates into (zeroed when tick t closes its bucket). */
export function nextSlot(d: SimDefs, t: Int): Int {
  return floorDiv(t, d.economy.galleryReadout.bucketTicks) % ringSlots(d);
}

/** Sum of the closed slots of a ring as seen after tick T. */
export function closedSum(d: SimDefs, ring: readonly Int[], tAfter: Int): Int {
  const open = nextSlot(d, tAfter);
  let sum = 0;
  for (let i = 0; i < ring.length; i++) if (i !== open) sum += ring[i] ?? 0;
  return sum;
}

/** Ticks covered by the full closed window. */
export function windowTicks(d: SimDefs): Int {
  return d.economy.galleryReadout.buckets * d.economy.galleryReadout.bucketTicks;
}
